import { ResponseCode } from '@/common/request/types';
import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_LOCALE } from '@/modules/locale/router';
import localeService from '@/services/locale';
import { computed, reactive } from 'vue';

export function useLocale() {
    const crudAction = useCrudActions();

    // #region for list view
    const { query, updateQueryToRoute } = useRouteQuery({
        name: ROUTE_NAME_LOCALE,
        definition: {
            criteria: { type: 'string', default: '' },
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const { list, total, fetchList } = useBaseListView({
        request: localeService.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute
        }
    });

    const actions = {
        add: crudAction.excuteAndNotify({
            request: localeService.add,
            onSuccess: () => fetchList()
        }),
        publish: crudAction.excuteAndNotify({
            request: localeService.publish,
            onSuccess: () => fetchList()
        }),
        unpublish: crudAction.excuteAndNotify({
            request: localeService.unpublish,
            onSuccess: () => fetchList()
        }),
        setDefault: crudAction.executeAction({
            request: localeService.setDefault,
            onSuccess: () => fetchList()
        }),
        remove: crudAction.confirmAndRemove({
            request: localeService.remove,
            onSuccess: () => fetchList()
        })
    };
    // #endregion

    const appLocaleSupported = useLocaleSupportedOptions(localeService.listSupported);
    const systemLocaleSupported = useLocaleSupportedOptions(localeService.listSystemSupported);

    return {
        list,
        total,
        fetchList,
        query,
        actions,
        appLocaleSupported,
        systemLocaleSupported,
        // #region TODO: use appLocaleSupported instead of
        localeSupportedOptions: appLocaleSupported.options,
        getListSupported: appLocaleSupported.load
        // #endregion
    };
}

function useLocaleSupportedOptions(request: () => Promise<{ code: number; data: any }>) {
    const model = reactive({
        list: [],
        get hasData() {
            return model.list.length > 0;
        },
        get options() {
            return model.list.map(l => ({
                label: l.language,
                value: l.id
            }));
        }
    });

    // TODO: remove - use model.options instead of
    const options = computed(() =>
        model.list.map(l => ({
            label: l.language,
            value: l.id
        }))
    );

    const load = async (force: boolean = true) => {
        if (!force && model.hasData) return;

        const response = await request();
        if (response.code === ResponseCode.Ok) {
            model.list = response.data;
        } else {
            model.list = [];
        }
    };

    return { load, model, options };
}
